import { Actions } from "../../constants/actions";
import { ModalNestedPosition } from "../../util/modal-nested-position";
import { FormBase } from "../../util/form-base-form";

class ModalSearchBaseForm extends FormBase {
    public getActionElement(target: EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(target, "[data-action]", HTMLElement);
    }
}

const modalSearchBaseForm = new ModalSearchBaseForm();

function getActionElement(target: EventTarget | Element | null | undefined): HTMLElement | null {
    if (!(target instanceof Element)) {
        return null;
    }

    const actionElement = modalSearchBaseForm.getActionElement(target);
    return actionElement instanceof HTMLElement ? actionElement : null;
}

function isMobileViewport(maxWidth = 768): boolean {
    const width = Number(maxWidth);
    const breakpoint = Number.isFinite(width) ? Math.max(0, Math.trunc(width)) : 768;

    try {
        if (typeof globalThis?.matchMedia === "function") {
            return globalThis.matchMedia(`(max-width: ${breakpoint}px)`).matches;
        }
    } catch {
        // Fall back to innerWidth check.
    }

    return typeof globalThis?.innerWidth === "number" ? globalThis.innerWidth <= breakpoint : false;
}

export type ModalSearchSelectionMode = "single" | "multi";

export interface ModalSearchAdapter {
    render?: (base: ModalSearchBase) => void;
    handleAction?: (base: ModalSearchBase, action: string, actionEl: HTMLElement, event: Event) => boolean | void;
}

export interface ModalSearchBaseParams {
    overlayEl: HTMLElement;
    modalEl: HTMLElement;
    adapter?: ModalSearchAdapter;
    filterInputEl?: HTMLInputElement | null;
}

export class ModalSearchBase {
    public overlayEl: HTMLElement;
    public modalEl: HTMLElement;
    public adapter: ModalSearchAdapter;
    public filterInputEl: HTMLInputElement | null;
    public data: unknown;
    public query: string;
    public selectedIds: Set<string>;
    public onSelectionChange: ((ids: string[]) => void) | null;
    private _bodyOverflowRestore: string;
    public isOpen: boolean;
    public selectionMode: ModalSearchSelectionMode;
    public closeOnSelect: boolean;

    public constructor(params: ModalSearchBaseParams) {
        this.overlayEl = params.overlayEl;
        this.modalEl = params.modalEl;
        this.adapter = params.adapter || {};
        this.filterInputEl = params.filterInputEl || null;

        this.data = this.createDataSlot();
        this.query = this.createQuerySlot();
        this.selectedIds = this.createSelectedIdsSet();
        this.onSelectionChange = this.createSelectionChangeHandler();
        this._bodyOverflowRestore = this.createBodyOverflowRestoreSlot();
        this.isOpen = this.createOpenState();
        this.selectionMode = this.createSelectionMode();
        this.closeOnSelect = this.createCloseOnSelectFlag();
    }

    public createDataSlot(): unknown {
        return null;
    }

    public createQuerySlot(): string {
        return "";
    }

    public createSelectedIdsSet(): Set<string> {
        return new Set();
    }

    public createSelectionChangeHandler(): ((ids: string[]) => void) | null {
        return null;
    }

    public createBodyOverflowRestoreSlot(): string {
        return "";
    }

    public createOpenState(): boolean {
        return false;
    }

    public createSelectionMode(): ModalSearchSelectionMode {
        return "multi";
    }

    public createCloseOnSelectFlag(): boolean {
        return false;
    }

    public setSelectionMode(mode: ModalSearchSelectionMode): void {
        this.selectionMode = mode === "single" ? "single" : "multi";

        if (this.selectionMode === "single" && this.selectedIds.size > 1) {
            const last = Array.from(this.selectedIds.values()).slice(-1)[0];
            this.selectedIds.clear();
            if (last) this.selectedIds.add(last);
        }
    }

    public setCloseOnSelect(value: boolean): void {
        this.closeOnSelect = !!value;
    }

    public setData(data: unknown): void {
        this.data = data;
    }

    public setQuery(query: string): void {
        this.query = String(query || "");
    }

    public setSelectedIds(ids: string[]): void {
        this.selectedIds.clear();
        (Array.isArray(ids) ? ids : []).forEach((id) => {
            const value = String(id ?? "").trim();
            if (!value) return;
            this.selectedIds.add(value);
        });

        if (this.selectionMode === "single" && this.selectedIds.size > 1) {
            const last = Array.from(this.selectedIds.values()).slice(-1)[0];
            this.selectedIds.clear();
            if (last) this.selectedIds.add(last);
        }
    }

    public getSelectedIds(): string[] {
        return Array.from(this.selectedIds);
    }

    public hasSelectedId(id: string): boolean {
        const value = String(id || "").trim();
        if (!value) return false;
        return this.selectedIds.has(value);
    }

    public areAllSelected(ids: string[]): boolean {
        const rows = Array.isArray(ids) ? ids : [];
        if (rows.length === 0) return false;
        return rows.every((id) => this.hasSelectedId(id));
    }

    public commitSelection(options: { render?: boolean } = {}): void {
        const render = options.render !== false;
        this.onSelectionChange?.(this.getSelectedIds());
        if (render) this.render();
    }

    public addSelectedIds(ids: string[]): boolean {
        if (this.selectionMode === "single") return false;
        const rows = Array.isArray(ids) ? ids : [];
        let changed = false;
        rows.forEach((id) => {
            const value = String(id || "").trim();
            if (!value) return;
            if (this.selectedIds.has(value)) return;
            this.selectedIds.add(value);
            changed = true;
        });
        return changed;
    }

    public removeSelectedIds(ids: string[]): boolean {
        if (this.selectionMode === "single") return false;
        const rows = Array.isArray(ids) ? ids : [];
        let changed = false;
        rows.forEach((id) => {
            const value = String(id || "").trim();
            if (!value) return;
            if (!this.selectedIds.delete(value)) return;
            changed = true;
        });
        return changed;
    }

    public setOnSelectionChange(callback: ((ids: string[]) => void) | null): void {
        this.onSelectionChange = typeof callback === "function" ? callback : null;
    }

    public render(): void {
        if (typeof this.adapter.render === "function") {
            this.adapter.render(this);
        }
    }

    public isMobileViewport(): boolean {
        return isMobileViewport(768);
    }

    public open(): void {
        this.isOpen = true;
        this._bodyOverflowRestore = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        ModalNestedPosition.openOverlayModalAsNested({
            overlayEl: this.overlayEl,
            modalEl: this.modalEl,
            nestedClassName: "is-nested-modal",
            deferVisibleClass: true,
        });

        this.render();

        if (this.filterInputEl) {
            this.filterInputEl.value = this.query;
            setTimeout(() => this.filterInputEl?.focus?.(), 0);
        }
    }

    public close(): void {
        this.isOpen = false;
        this.modalEl.classList.remove("is-nested-modal");
        this.modalEl.style.removeProperty("--nested-offset-x");
        this.modalEl.style.removeProperty("--nested-offset-y");
        delete this.modalEl.dataset.modalDepth;
        ModalNestedPosition.closeOverlayModal({
            overlayEl: this.overlayEl,
            modalEl: this.modalEl,
            hideDelayMs: 200,
        });

        document.body.style.overflow = this._bodyOverflowRestore || "";
    }

    public handleOverlayClick(event: Event): void {
        if (event.target !== this.overlayEl) return;
        this.close();
    }

    public handleCloseClick(event: Event): void {
        event.preventDefault?.();
        event.stopPropagation?.();
        this.close();
    }

    public handleFilterInput(_event: InputEvent): void {
        if (!this.filterInputEl) return;
        this.query = String(this.filterInputEl.value || "");
        this.render();
    }

    public handleActionClick(event: MouseEvent): void {
        event.stopPropagation();

        const target = event.target instanceof HTMLElement ? event.target : null;
        if (!target) return;

        const actionEl = getActionElement(target);
        if (!(actionEl instanceof HTMLElement)) return;

        const action = String(actionEl.getAttribute("data-action") || "").trim();
        if (!action) return;

        if (action === Actions.CLOSE || action === Actions.CONFIRM) {
            this.close();
            return;
        }

        if (action === Actions.CLEAR) {
            this.selectedIds.clear();
            this.query = "";
            if (this.filterInputEl) this.filterInputEl.value = "";
            this.commitSelection({ render: true });
            return;
        }

        const handled = this.adapter.handleAction?.(this, action, actionEl, event);
        if (handled === true) return;
    }

    public toggleSelectedId(id: string): void {
        const value = String(id || "").trim();
        if (!value) return;

        if (this.selectionMode === "single") {
            const isAlreadySelected = this.selectedIds.has(value);
            this.selectedIds.clear();
            if (!isAlreadySelected) this.selectedIds.add(value);
        } else if (this.selectedIds.has(value)) {
            this.selectedIds.delete(value);
        } else {
            this.selectedIds.add(value);
        }

        this.commitSelection({ render: false });

        if (this.closeOnSelect && this.selectionMode === "single" && this.selectedIds.size === 1) {
            this.close();
        }
    }
}
